import { Context, FirebaseV9 } from '@bitiotic/bitiotic';
import {
    AddPlayerToGameArgs, AddPlayerToGameResult, CloseGameRoundArgs, CloseGameRoundResult, CreateGlobalUserArgs, CreateGlobalUserResult,
    LayoutRoundGuessesArgs, LayoutRoundGuessesResult, Lobby, NewGameRoundArgs, NewGameRoundResult, Persistent,
    SetGameOverArgs, SetGameOverResult, SetRoundWinnersArgs, SetRoundWinnersResult, StartNewGameArgs, StartNewGameResult,
} from '@bitiotic/numbet';
import { createResource, createSignal, For } from 'solid-js';
import { JSX } from 'solid-js/jsx-runtime';
import { SpinButton, StandardButton } from './Widgets';

type AllResultTypes = AddPlayerToGameResult | CloseGameRoundResult |
    CreateGlobalUserResult | LayoutRoundGuessesResult | NewGameRoundResult |
    SetGameOverResult | SetRoundWinnersResult | StartNewGameResult;

const vlargeString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:\'",.<>?/`~ '.repeat(100);

const SAFE_LOG_STR_OPTS = { longstringlen: 63, longstring: 'length', null: '<null>', indent: undefined } as const;
const SAFE_UI_STR_OPTS = { longstringlen: 63, longstring: 'length', null: '<null>', indent: 3 } as const;

interface ValidIdentifiers {
    playerPath: Persistent.PlayerPath,
    gamePath: Persistent.GamePath,
    gameOptions: Persistent.GameOptions,
    roundPath: Persistent.RoundPath,
    roundNumber: number,
    validLooking: string,
}


function expectRes<ResultType extends AllResultTypes>(ctx: Context, res: ResultType, testCase: { label: string, ok?: boolean }): void {
    if (res.status !== 'ok' && res.status !== 'failed') {
        throw ctx.newError('Bad status in result:', res);
    }
    if (testCase.ok && res.status !== 'ok') {
        throw ctx.newError(`Case ${testCase.label}: Expected OK result, got: `, res);
    }
    if (!testCase.ok && res.status !== 'failed') {
        throw ctx.newError(`Case ${testCase.label}: Expected "failed" result, got: `, res);
    }
}

interface TestCase<ArgsType> {
    label: string;
    ok?: boolean;

    args: ArgsType;
}

function testCloudFunc<Args, Result extends AllResultTypes>(
    ctx: Context,
    firebase: FirebaseV9.Firebase,
    funcName: string,
    testCase: TestCase<Args>,
): Promise<string> {
    const cloudFunc = firebase.funcClient.findCallable<Args, Result>(funcName);

    return cloudFunc(testCase.args)
        .then(res => {
            ctx.log(`Test: ${funcName}(${ctx.safeString(testCase, SAFE_LOG_STR_OPTS)}): passed: `, res);
            expectRes<Result>(ctx, res, testCase);
            return 'Passed' + (testCase.ok ? ' (ok)' : ' (err)');
        },
            err => {
                ctx.log(`Test: ${funcName}(${ctx.safeString(testCase, SAFE_LOG_STR_OPTS)}): UNEXPECTED ERROR: `, err);
                throw err;
            });
}

function TestButton(props: {
    children: JSX.Element
    ref: (el: HTMLButtonElement) => void,
    onClick: () => Promise<void>,
}): JSX.Element {
    return <button
        class="m-3 border p-2 bg-blue-200 hover:bg-blue-400"
        ref={props.ref}
        onClick={props.onClick}
    >
        {props.children}
    </button>;
}


function DeclareTest<T>(props: {
    ctx: Context,
    testCase: TestCase<T>,
    ref: (el: HTMLButtonElement) => void,
    invoke: (arg: TestCase<T>) => Promise<string>
}): JSX.Element {
    const [getResult, setResult] = createSignal<string>('no results yet');
    const [getCount, setCount] = createSignal<number>(0);

    return <tr>
        <td>
            <TestButton
                ref={props.ref}
                onClick={() => {
                    setResult('running...');
                    return props.invoke(props.testCase)
                        .then(res => {
                            setResult(res);
                            setCount(getCount() + 1);
                        })
                        .catch(err => {
                            setResult('Error: ' + err);
                        });
                }}
            >Test #{getCount() + 1}</TestButton>
        </td>
        <td>
            {props.testCase.label}
        </td>
        <td>
            <pre class="whitespace-pre text-sm">
                {props.ctx.safeString(props.testCase.args, SAFE_UI_STR_OPTS)}
            </pre>
        </td>
        <td>
            <span class="font-mono">{getResult()}</span>
        </td>
    </tr>;
}


function DeclareTests<T>(props: {
    ctx: Context,
    cases: TestCase<T>[],
    invoke: (arg: TestCase<T>) => Promise<string>
    children: JSX.Element,
    ref: (el: HTMLButtonElement) => void,
}): JSX.Element {
    const buttonRefs: HTMLButtonElement[] = [];

    return <>
        <tr>
            <th>Test Section {props.children}</th>
            <td>
                <TestButton
                    ref={props.ref}
                    onClick={() => {
                        const head = Promise.resolve();
                        let chain = head;
                        for (const b of buttonRefs) {
                            chain = chain.then(() => b.click());
                        }
                        return head;
                    }}
                >Run all {props.children}</TestButton>
            </td>
            <td></td>
            <td></td>
        </tr>
        <For each={props.cases}>
            {(testCase) => (<DeclareTest<T>
                ctx={props.ctx}
                ref={(child) => buttonRefs.push(child)}
                testCase={testCase}
                invoke={props.invoke}
            />)}
        </For>
    </>;
}

// Define the interesting boundary cases for *invalid* test IDs here.
function loadInvalidIDs(): string[] {
    return [
        '',
        // "\n", // this is valid ID, actually
        // "\t  \n", // also valid
        '__this-is-reserved__',
        '.',
        '..',
        vlargeString,
        'user_id_danger/../..-dot-attack',
        null as unknown as string,
        undefined as unknown as string,
        99 as unknown as string,
        { foo: 'bar' } as unknown as string,
    ];
}

function randomID(base: string): string {
    const randID = Math.floor(1 * 1000 * 1000 * Math.random());
    return `rando-${base}-id-${randID}`;
}


// Create Valid IDs (game, round, player, etc) that can be used for test cases.
async function loadValidIDs(ctx: Context, firebase: FirebaseV9.Firebase, playerPath: Persistent.PlayerPath): Promise<ValidIdentifiers> {
    const validGameID = Persistent.asGamePath(randomID('game'));

    const newGameOpts: Persistent.GameOptions = {
        managerIsPlayer: true,
        maxPlayerCount: 6,
        betSpotCount: 5,
        betUnit: 100,
        betGrantCount: 3,
        betsPerRound: 2,
        bestAnswerGrantCount: 2,
        roundsPerGame: 10,
        collapseDuplicateAnswers: true,
    };
    const newGameArgs: TestCase<StartNewGameArgs> = {
        label: 'create valid new game',
        ok: true,
        args: {
            gamePath: validGameID,
            options: newGameOpts,
            manager: playerPath,
        }
    };

    // Create a new game to get a valid game ID
    await testCloudFunc<StartNewGameArgs, StartNewGameResult>(ctx.newCtx('newGame'), 'startNewGame', newGameArgs);

    const roundNumber = 0;
    const newRoundArgs: TestCase<NewGameRoundArgs> = {
        label: 'create valid round',
        ok: true,
        args: {
            gamePath: validGameID,
            roundNumber,
        },
    };

    // Create a new round to get a valid round ID
    const newRound = await testCloudFunc<NewGameRoundArgs, NewGameRoundResult>(ctx.newCtx('newRound'), 'newGameRound', newRoundArgs);

    return {
        playerPath,
        gamePath: validGameID,
        gameOptions: newGameOpts,
        roundPath: newRound.roundPath,
        roundNumber,
        validLooking: 'fuz8l6YmvPiOeQQaPxRV',  // doc ID from a wiped server
    };

    async function testCloudFunc<ArgsT, ResT extends AllResultTypes>(ctx: Context, funcName: string, args: TestCase<ArgsT>): Promise<ResT> {
        const cloudFunc = firebase.funcClient.findCallable<ArgsT, ResT>(funcName);

        return cloudFunc(args.args)
            .then(res => {
                expectRes<ResT>(ctx, res, { label: 'setup', ok: true });
                return res;
            },
                err => {
                    ctx.log(`Test: ${funcName}(${ctx.safeString(args, SAFE_LOG_STR_OPTS)}): UNEXPECTED ERROR: `, err);
                    throw err;
                });
    }
}

function createGlobalUserTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<CreateGlobalUserArgs>[] {
    if (valid === undefined) {
        return [];
    }

    const playerPath = valid.playerPath;

    return [
        {
            label: 'Existing',
            ok: true,
            args: { userID: playerPath, displayName: 'Test Display Name' },
        },
        {
            label: 'Existing w/Extras',
            args: {
                userID: playerPath,
                displayName: 'Test Display Name',
                extra: vlargeString,
            } as CreateGlobalUserArgs,
        },

        ...invalidIDs.map(id => {
            return {
                label: `Test userID="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    userID: id,
                    displayName: 'ignored',
                },
            };
        }),

        {
            label: 'GameID in userID',
            args: {
                userID: valid.gamePath,
                displayName: 'ignored',
            }
        },

        {
            label: 'Empty displayName',
            args: {
                userID: Persistent.asPlayerPath(randomID('player')),
                displayName: ''
            },
        },
        {
            label: 'Very long displayName',
            args: {
                userID: Persistent.asPlayerPath(randomID('player')),
                displayName: vlargeString
            }
        },
        {
            label: 'No parameters',
            args: {} as CreateGlobalUserArgs,
        },

    ];
}

function startNewGameTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<StartNewGameArgs>[] {
    if (valid === undefined) {
        return [];
    }

    function newGameID(): Persistent.GamePath {
        return Persistent.asGamePath(randomID('game'));
    }

    const gameOptDiffs: Partial<Persistent.GameOptions>[] = [
        { maxPlayerCount: 0 },
        { maxPlayerCount: 100 },
        { betSpotCount: 0 },
        { betSpotCount: 1 },
        { betUnit: -100 },
        { betGrantCount: -5 },
        { betsPerRound: 0 },
        { bestAnswerGrantCount: -2 },
        { roundsPerGame: 0 },
    ];

    return [
        {
            label: 'existing',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                options: valid.gameOptions,
                manager: valid.playerPath,
            },
        },
        {
            label: 'existing, extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                options: valid.gameOptions,
                manager: valid.playerPath,
                extraArg: 'extraneous',
            } as unknown as StartNewGameArgs,
        },
        {
            label: 'existing, bad manager',
            args: {
                gamePath: valid.gamePath,
                options: valid.gameOptions,
                manager: Persistent.asPlayerPath('nonexistent-player'),
            },
        },
        {
            label: 'existing, empty options',
            args: {
                gamePath: valid.gamePath,
                options: {} as Persistent.GameOptions,
                manager: valid.playerPath,
            },
        },
        {
            label: 'new game, empty options',
            args: {
                gamePath: newGameID(),
                options: {} as Persistent.GameOptions,
                manager: valid.playerPath,
            },
        },

        ...invalidIDs.map(id => ({
            label: `new game, gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: Persistent.asGamePath(id),
                options: valid.gameOptions,
                manager: valid.playerPath,
            },
        })),

        ...invalidIDs.map(id => ({
            label: `new game, manager="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: newGameID(),
                options: valid.gameOptions,
                manager: Persistent.asPlayerPath(id),
            },
        })),

        ...gameOptDiffs.map(diff => ({
            label: `new game, options diff ${ctx.safeString(diff, SAFE_UI_STR_OPTS)}`,
            args: {
                gamePath: newGameID(),
                options: {
                    ...valid.gameOptions,
                    ...diff,
                },
                manager: valid.playerPath,
            },
        })),
    ];
}

function addPlayerToGameTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<AddPlayerToGameArgs>[] {
    if (valid === undefined) {
        return [];
    }

    // BAH.  Its really hard to get a handle on a second valid player that is
    // part of the game.  This direct fabrication of a player fails because the
    // player doesn't have a RequestToJoin on the Game.
    //
    // const p2 = Persistent.asPlayerPath('player2');

    return [
        {
            label: 're-add manager',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                playerPath: valid.playerPath,
                isActive: true,
            },
        },
        /*
        {
            label: 'add p2',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                playerPath: p2,
                isActive: true,
            },
        },
        {
            label: 're-add p2',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                playerPath: p2,
                isActive: true,
            },
        },
        */
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                playerPath: valid.playerPath,
                isActive: true,
                extraArg: 'extraneous',
            } as unknown as AddPlayerToGameArgs,
        },
        {
            label: 'swap ids',
            args: {
                gamePath: valid.playerPath as unknown as Persistent.GamePath,
                playerPath: valid.gamePath as unknown as Persistent.PlayerPath,
                isActive: true,
            },
        },
        ...invalidIDs.map(id => ({
            label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: Persistent.asGamePath(id),
                playerPath: valid.playerPath,
                isActive: true,
            },
        })),
        ...invalidIDs.map(id => ({
            label: `invalid playerPath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: valid.gamePath,
                playerPath: Persistent.asPlayerPath(id),
                isActive: true,
            },
        })),
    ];
}

function newGameRoundTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<NewGameRoundArgs>[] {
    if (valid === undefined) {
        return [];
    }

    return [
        {
            label: 'existing',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundNumber: valid.roundNumber,
            },
        },
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                roundNumber: valid.roundNumber,
                extraArg: 'extraneous',
            } as unknown as NewGameRoundArgs,
        },
        {
            label: 'negative round number',
            args: {
                gamePath: valid.gamePath,
                roundNumber: -1,
            },
        },
        {
            label: 'bogus round number',
            args: {
                gamePath: valid.gamePath,
                roundNumber: 109292,
            },
        },
        ...invalidIDs.map(id => ({
            label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: Persistent.asGamePath(id),
                roundNumber: 1,
            },
        })),
    ];
}

function layoutRoundGuessesTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<LayoutRoundGuessesArgs>[] {
    if (valid === undefined) {
        return [];
    }

    return [
        {
            label: 'valid',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
            },
        },
        {
            label: 're-run valid',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
            },
        },
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                extraArg: 'extraneous',
            } as unknown as LayoutRoundGuessesArgs,
        },
        ...invalidIDs.flatMap(id => [
            {
                label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: Persistent.asGamePath(id),
                    roundPath: valid.roundPath,
                },
            },
            {
                label: `invalid roundPath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: valid.gamePath,
                    roundPath: Persistent.asRoundPath(id),
                },
            },
        ]),
    ];
}

function closeGameRoundTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<CloseGameRoundArgs>[] {
    if (valid === undefined) {
        return [];
    }

    return [
        {
            label: 'valid',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
            },
        },
        {
            label: 're-run valid',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
            },
        },
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                extraArg: 'extraneous',
            } as unknown as CloseGameRoundArgs,
        },
        ...invalidIDs.flatMap(id => [
            {
                label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: Persistent.asGamePath(id),
                    roundPath: valid.roundPath,
                },
            },
            {
                label: `invalid roundPath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: valid.gamePath,
                    roundPath: Persistent.asRoundPath(id),
                },
            },
        ]),
    ];
}

function setRoundWinnerTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<SetRoundWinnersArgs>[] {
    if (valid === undefined) {
        return [];
    }

    const validBetPath = Persistent.makeBetSpotPath(1);

    const p1 = valid.playerPath;

    // Bogus player, but doesn't seem to trip anything up... I guess that's okay?
    const p2 = Persistent.asPlayerPath('extra-player-1');

    return [
        {
            label: 'valid',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                winningSpotPath: validBetPath,
                answer: 42,
                guesses: {
                    [p1]: {
                        guessValue: 40,
                        playerPath: p1,
                        rawGuess: '40',
                    },
                    [p2]: {
                        guessValue: 45,
                        playerPath: p2,
                        rawGuess: '45',
                    },
                },
            },
        },
        {
            label: 'valid, no guesses',
            ok: true,
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                winningSpotPath: validBetPath,
                answer: 42,
                guesses: {},
            },
        },
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                winningSpotPath: validBetPath,
                answer: 42,
                guesses: {},
                extraArg: 'extraneous',
            } as unknown as SetRoundWinnersArgs,
        },
        {
            label: 'negative answer',
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                winningSpotPath: validBetPath,
                answer: -1,
                guesses: {},
            },
        },
        {
            label: 'string answer',
            args: {
                gamePath: valid.gamePath,
                roundPath: valid.roundPath,
                winningSpotPath: validBetPath,
                answer: '99' as unknown as number,
                guesses: {},
            },
        },
        ...invalidIDs.flatMap(id => [
            {
                label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: Persistent.asGamePath(id),
                    roundPath: valid.roundPath,
                    winningSpotPath: validBetPath,
                    answer: 42,
                    guesses: {},
                },
            },
            {
                label: `invalid roundPath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: valid.gamePath,
                    roundPath: Persistent.asRoundPath(id),
                    winningSpotPath: validBetPath,
                    answer: 42,
                    guesses: {},
                },
            },
            /* winning spot isn't strictly validated ...
            {
                label: `invalid winningSpotPath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
                args: {
                    gamePath: valid.gamePath,
                    roundPath: valid.roundPath,
                    winningSpotPath: Persistent.asBetSpotPath(id),
                    answer: 42,
                    guesses: {},
                },
            },
            */
        ]),
        {
            label: 'empty',
            args: {
                gamePath: Persistent.asGamePath(''),
                roundPath: Persistent.asRoundPath(''),
                winningSpotPath: Persistent.asBetSpotPath(''),
                answer: 99,
                guesses: {},
            },
        },
    ];
}

function setGameOverTestCases(ctx: Context, valid: ValidIdentifiers | undefined, invalidIDs: string[]): TestCase<SetGameOverArgs>[] {
    if (valid === undefined) {
        return [];
    }

    return [
        {
            label: 'valid',
            ok: true,
            args: {
                gamePath: valid.gamePath
            },
        },
        {
            label: 'valid, but again',
            ok: true,
            args: {
                gamePath: valid.gamePath
            },
        },
        {
            label: 'extraneous top-level arg',
            args: {
                gamePath: valid.gamePath,
                extraArg: 'extraneous',
            } as unknown as SetGameOverArgs,
        },
        {
            label: 'player as game',
            args: {
                gamePath: valid.playerPath as unknown as Persistent.GamePath,
            },
        },
        ...invalidIDs.map(id => ({
            label: `invalid gamePath="${ctx.safeString(id, SAFE_UI_STR_OPTS)}"`,
            args: {
                gamePath: Persistent.asGamePath(id),
            },
        })),
    ];
}

export function SelfTest(props: {
    ctx: Context,
    firebase: FirebaseV9.Firebase,
    lobby: Lobby,
    playerPath: Persistent.PlayerPath | 'unknown',
}): JSX.Element {
    const playerPath = props.playerPath; // becomes immutable
    if (playerPath === 'unknown') {
        throw props.ctx.newError('SelfTest: playerPath is "unknown"');
    }

    const invalidIDs = loadInvalidIDs();

    const [validIDs] = createResource(() => loadValidIDs(props.ctx.newCtx('loadValid'), props.firebase, playerPath));

    const sectionButtons: HTMLButtonElement[] = [];

    return <>
        <div class="text-xl bold">Button Tests</div>

        <div>
            <StandardButton
                onClick={() => { props.ctx.log('Green click'); }}
                color='green'
            >Green Button</StandardButton>
        </div>
        <div>
            <StandardButton
                onClick={() => { props.ctx.log('Red click'); }}
                color='red'
            >Red Button</StandardButton>
        </div>
        <div>
            <StandardButton
                onClick={() => { throw props.ctx.newError('Red click disabled'); }}
                color='red'
                disabled={true}
            >Disabled Red Button</StandardButton>
        </div>
        <div>
            <StandardButton
                onClick={() => { props.ctx.log('Green click disabled'); }}
                color='green'
                disabled={true}
            >Disabled Green Button</StandardButton>
        </div>
        <div>
            <SpinButton
                onClick={() => {
                    return new Promise(resolve => {
                        setTimeout(resolve, 3000);
                    });
                }}
                color='green'
            >Make me Spin</SpinButton>
        </div>
        <div class="text-xl bold">Cloud Function API Tests</div>

        <div>
            PlayerPath:&nbsp;
            <span class="text-sm font-mono">
                {props.playerPath}
            </span>
        </div>

        <table>
            <DeclareTests<CreateGlobalUserArgs>
                ctx={props.ctx.newCtx('createGlobalUser')}
                cases={createGlobalUserTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={(testCase) => {
                    return testCloudFunc<CreateGlobalUserArgs, CreateGlobalUserResult>
                        (props.ctx, props.firebase, 'createGlobalUser', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >createGlobalUser</DeclareTests>
            <DeclareTests<StartNewGameArgs>
                ctx={props.ctx.newCtx('startNewGame')}
                cases={startNewGameTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={(testCase) => {
                    return testCloudFunc<StartNewGameArgs, StartNewGameResult>
                        (props.ctx, props.firebase, 'startNewGame', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >testStartNewGame</DeclareTests >

            <DeclareTests<AddPlayerToGameArgs>
                ctx={props.ctx.newCtx('addPlayerToGame')}
                cases={addPlayerToGameTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={(testCase) => {
                    return testCloudFunc<AddPlayerToGameArgs, AddPlayerToGameResult>
                        (props.ctx, props.firebase, 'addPlayerToGame', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >addPlayerToGame</DeclareTests >

            <DeclareTests<NewGameRoundArgs>
                ctx={props.ctx.newCtx('newGameRound')}
                cases={newGameRoundTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={testCase => {
                    return testCloudFunc<NewGameRoundArgs, NewGameRoundResult>
                        (props.ctx, props.firebase, 'newGameRound', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >newGameRound</DeclareTests >

            <DeclareTests<LayoutRoundGuessesArgs>
                ctx={props.ctx.newCtx('layoutRoundGuesses')}
                cases={layoutRoundGuessesTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={testCase => {
                    return testCloudFunc<LayoutRoundGuessesArgs, LayoutRoundGuessesResult>
                        (props.ctx, props.firebase, 'layoutRoundGuesses', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >layoutRoundGuesses</DeclareTests >

            <DeclareTests<CloseGameRoundArgs>
                ctx={props.ctx.newCtx('closeGameRound')}
                cases={closeGameRoundTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={testCase => {
                    return testCloudFunc<CloseGameRoundArgs, CloseGameRoundResult>
                        (props.ctx, props.firebase, 'closeGameRound', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >closeGameRound</DeclareTests >

            <DeclareTests<SetRoundWinnersArgs>
                ctx={props.ctx.newCtx('setRoundWinners')}
                cases={setRoundWinnerTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={testCase => {
                    return testCloudFunc<SetRoundWinnersArgs, SetRoundWinnersResult>
                        (props.ctx, props.firebase, 'setRoundWinners', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >setRoundWinners</DeclareTests >

            <DeclareTests<SetGameOverArgs>
                ctx={props.ctx.newCtx('setGameOver')}
                cases={setGameOverTestCases(props.ctx, validIDs(), invalidIDs)}
                invoke={testCase => {
                    return testCloudFunc<SetGameOverArgs, SetGameOverResult>
                        (props.ctx, props.firebase, 'setGameOver', testCase);
                }}
                ref={el => sectionButtons.push(el)}
            >setGameOver</DeclareTests >
        </table>
    </>;
}